import { fieldValue, isArchived, isDeleted } from './merge.js';
import { sumByCurrency } from './money.js';
import { daysSinceReview, today } from './review.js';
/**
 * Items decided for release but not yet gone (FR-7.1).
 *
 * An item that already has a release outcome is out of the queue by definition:
 * it is archived, and FR-7.4 makes the existence of that outcome the thing that
 * archives it. There is no separate "done" flag to disagree with.
 */
export function releaseQueue(manifest, asOf = today()) {
    const queued = [];
    for (const item of manifest.items.values()) {
        if (isDeleted(item) || isArchived(item))
            continue;
        if (item.currentIntention !== 'release')
            continue;
        queued.push({ item, daysWaiting: daysSinceReview(item, asOf) });
    }
    // Longest waiting first: the point of the queue is the thing that has been
    // sitting in it, not the thing decided this morning.
    return queued.sort((a, b) => (b.daysWaiting ?? 0) - (a.daysWaiting ?? 0));
}
/** FR-7.7: a user-set threshold, so "too long" is their judgment rather than ours. */
export const isStale = (entry, afterDays) => entry.daysWaiting !== null && entry.daysWaiting >= afterDays;
/**
 * What counts as released, whatever window is being asked about.
 *
 * Factored out so "between two dates" and "since a date" cannot drift into
 * disagreeing about which items are released at all -- the window is the only
 * thing that should differ between them. A deleted item is excluded either way:
 * it left the catalog by a different door, and counting it as released would
 * put it in the release report's totals (FR-7.6) as well.
 */
function releasedWhere(manifest, matches) {
    const released = [];
    for (const item of manifest.items.values()) {
        if (isDeleted(item) || !isArchived(item))
            continue;
        const release = item.release?.v;
        if (release === undefined)
            continue;
        if (!matches(release))
            continue;
        released.push({ item, release });
    }
    return released.sort((a, b) => b.release.onDate.localeCompare(a.release.onDate));
}
/** Items whose release was recorded within a period, by the date on the outcome. */
export function releasedBetween(manifest, from, to) {
    // Calendar dates compare correctly as strings, which is part of why they are
    // stored that way (NFR-FUNC-4).
    return releasedWhere(manifest, (release) => release.onDate >= from && release.onDate <= to);
}
/**
 * Items released strictly after a date.
 *
 * Strictly, because the look-back's "owned then" already counts the whole of
 * that day: an item released on the date itself was already gone from the
 * figure it is being compared against, and counting it as having left since
 * would subtract it twice.
 */
export function releasedAfter(manifest, after) {
    return releasedWhere(manifest, (release) => release.onDate > after);
}
/**
 * What left, and what it was worth (FR-7.6).
 *
 * Proceeds and original purchase value are reported separately and never netted
 * against each other. The difference between them is not a loss -- it is what
 * owning the thing cost, which is a different statement and not one this report
 * is entitled to make (NFR-SAFE-1).
 */
export function releaseReport(manifest, from, to) {
    const released = releasedBetween(manifest, from, to);
    const byOutcome = {
        donated: 0,
        sold: 0,
        gifted: 0,
        recycled: 0,
        discarded: 0,
        returned: 0,
    };
    for (const entry of released)
        byOutcome[entry.release.outcome] += 1;
    return {
        count: released.length,
        proceeds: sumByCurrency(released.map((entry) => entry.release.proceeds)),
        purchaseValue: sumByCurrency(released.map((entry) => fieldValue(entry.item, 'purchasePrice') ?? undefined)),
        byOutcome,
    };
}
/** The last N days, as a pair of calendar dates, for the usual report periods. */
export function periodEnding(days, asOf = today()) {
    const at = new Date(`${asOf}T00:00:00Z`);
    at.setUTCDate(at.getUTCDate() - days);
    return { from: at.toISOString().slice(0, 10), to: asOf };
}
//# sourceMappingURL=release.js.map